import type { FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FunnelIcon } from '@phosphor-icons/react/dist/csr/Funnel';
import { XCircleIcon } from '@phosphor-icons/react/dist/csr/XCircle';

import { Button } from '../../primitives/Button/Button';
import styles from './FilterBar.module.css';

/**
 * A list screen's filters, in one card and — wherever the width allows — one
 * row: an optional leading control (a status `SegmentedControl`), the
 * fields, then Filter and Clear.
 *
 * Every list screen used to build this itself, and each spent its own
 * vertical space doing it: the status control on one row, Export on the
 * next, the fields on a third, each separated by a full section gap, and
 * all of it on the page ground rather than on a card. One composite means
 * one rhythm, and the buttons carry the same words and icons everywhere.
 *
 * `note` is for a standing fact about the list (the audit trail's "not
 * verified" caveat): inside the card, at a legible size, rather than as
 * caption text floating over the background image.
 */
export interface FilterBarProps {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClear: () => void;
  /** Placed first in the row, before the fields — e.g. a status SegmentedControl. */
  leading?: ReactNode;
  note?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function FilterBar({ onSubmit, onClear, leading, note, children, className }: FilterBarProps) {
  const { t } = useTranslation();

  return (
    <form
      className={[styles['bar'], className].filter(Boolean).join(' ')}
      onSubmit={onSubmit}
      role="search"
    >
      <div className={styles['row']}>
        {leading === undefined ? null : <div className={styles['leading']}>{leading}</div>}
        {children}
        <div className={styles['actions']}>
          <Button type="submit" icon={<FunnelIcon size={16} />}>
            {t('actions.filter')}
          </Button>
          <Button type="button" variant="secondary" onClick={onClear} icon={<XCircleIcon size={16} />}>
            {t('actions.clearFilters')}
          </Button>
        </div>
      </div>
      {note === undefined ? null : <p className={styles['note']}>{note}</p>}
    </form>
  );
}
